import { useCallback, useEffect, useRef, useState } from "react";
import type { EmblaCarouselType } from "embla-carousel";
import { Carousel } from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Box, Flex, Text, UnstyledButton, rem, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { Link } from "react-router-dom";

/** ISO 3166-1 alpha-2 → regional indicator flag emoji (e.g. IN → India flag). */
function regionalIndicatorFlagEmoji(alpha2: string): string | null {
  const c = alpha2.toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return null;
  const base = 0x1f1e6;
  const letterA = 0x41;
  return String.fromCodePoint(
    base + (c.charCodeAt(0) - letterA),
    base + (c.charCodeAt(1) - letterA),
  );
}

const NAVY = "#001f3f";
const MUTED_TEXT = "#6b7280";
const CTA_BG = "#ffc107";
const LINE_GRAY = "#e5e7eb";

export type { DiscoverPromoSlide } from "@shared/discoverPromoSlide";
import type { DiscoverPromoSlide } from "@shared/discoverPromoSlide";

export type DiscoverPromoCarouselProps = {
  slides: DiscoverPromoSlide[];
  height?: number;
  autoplayDelayMs?: number;
  /** Tighter slide padding for compact desktop hero layout. */
  compact?: boolean;
  /** Stretch carousel to fill parent height (desktop hero beside title/toggle). */
  fillHeight?: boolean;
};

/** Horizontal padding inside slides when prev/next controls are shown (control + offset + buffer). */
const SLIDE_INSET_WITH_CONTROLS = rem(50);

function DiscoverPromoSlideCard({
  slide,
  multi,
  compact = false,
}: {
  slide: DiscoverPromoSlide;
  multi: boolean;
  compact?: boolean;
}) {
  const codes = slide.flags?.map((c) => c.toUpperCase()).filter(Boolean) ?? [];

  return (
    <Flex
      w="100%"
      maw="100%"
      mih={0}
      h="100%"
      direction="column"
      justify="center"
      align="center"
      gap={compact ? rem(4) : "sm"}
      px={multi ? (compact ? rem(40) : SLIDE_INSET_WITH_CONTROLS) : compact ? rem(8) : rem(12)}
      py={compact ? rem(4) : rem(10)}
      bg={slide.surfaceBg ?? "var(--mantine-color-body)"}
      style={{
        borderRadius: "var(--mantine-radius-md)",
        boxSizing: "border-box",
      }}
    >
      <Flex w="100%" maw="100%" miw={0} direction="column" align="center" gap="xs">
        {codes.length > 0 ? (
          <Flex gap={10} justify="center" align="center" wrap="nowrap">
            {codes.map((code) => {
              const flag = regionalIndicatorFlagEmoji(code);
              if (!flag) return null;
              return (
                <Text key={code} span fz={24} lh={1} role="img" aria-label={`${code} flag`}>
                  {flag}
                </Text>
              );
            })}
          </Flex>
        ) : null}
        <Text
          fw={700}
          size="sm"
          c={slide.headlineColor ?? "primary.7"}
          ta="center"
          maw="100%"
          lineClamp={3}
          style={{ wordBreak: "break-word" }}
        >
          {slide.headline}
        </Text>
        <Text
          size="xs"
          c={slide.descriptionColor ?? "slate.6"}
          lh={1.55}
          ta="center"
          maw="100%"
          style={{ wordBreak: "break-word" }}
        >
          {slide.description}
        </Text>
      </Flex>
    </Flex>
  );
}

/** Mobile-only: headline with first/last flags on the sides; no separate flag row. */
function DiscoverPromoSlideCardMobile({ slide }: { slide: DiscoverPromoSlide }) {
  const codes = slide.flags?.map((c) => c.toUpperCase()).filter(Boolean) ?? [];
  const left = codes[0] ? regionalIndicatorFlagEmoji(codes[0]) : null;
  const rightCode = codes.length > 1 ? codes[codes.length - 1] : codes[0];
  const right = rightCode ? regionalIndicatorFlagEmoji(rightCode) : null;

  return (
    <Flex
      w="100%"
      h="100%"
      direction="column"
      justify="center"
      align="center"
      miw={0}
      py={rem(8)} 
      px={rem(30)}
      bg="#fff"
      style={{ boxSizing: "border-box" }}
    >
      <Text
        fw={700}
        fz={rem(12)}
        lh={1.35}
        c={NAVY}
        ta="center"
        maw="100%"
        lineClamp={2}
        style={{ wordBreak: "break-word" }}
      >
        {left ? (
          <Text component="span" fz={rem(14)} lh={1} mr={6}>
            {left}
          </Text>
        ) : null}
        {slide.headline}
        {right ? (
          <Text component="span" fz={rem(14)} lh={1} ml={6}>
            {right}
          </Text>
        ) : null}
      </Text>
      <Text
        fz={rem(10)}
        mt={rem(2)}
        c={MUTED_TEXT}
        ta="center"
        maw="100%"
        lineClamp={2}
        style={{ wordBreak: "break-word" }}
      >
        {slide.description}
      </Text>
    </Flex>
  );
}

function CarouselNavButton({
  direction,
  onClick,
  disabled,
  label,
}: {
  direction: "prev" | "next";
  onClick: () => void;
  disabled?: boolean;
  label: string;
}) {
  const Icon = direction === "prev" ? IconChevronLeft : IconChevronRight;
  return (
    <UnstyledButton
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      w={rem(26)}
      h={rem(26)}
      style={{
        flexShrink: 0,
        borderRadius: "50%",
        border: `1px solid ${LINE_GRAY}`,
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.35 : 1,
        pointerEvents: disabled ? "none" : "auto",
        color: "#9ca3af",
      }}
    >
      <Icon size={13} stroke={2} />
    </UnstyledButton>
  );
}

export default function DiscoverPromoCarousel({
  slides,
  height = 200,
  autoplayDelayMs = 4500,
  compact = false,
  fillHeight = false,
}: DiscoverPromoCarouselProps) {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`, false, {
    getInitialValueInEffect: true,
  });

  const autoplay = useRef(
    Autoplay({
      delay: autoplayDelayMs,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: false,
    }),
  );

  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const multi = slides.length > 1;

  const onSelect = useCallback(() => {
    if (!embla) return;
    setActiveIndex(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("reInit", onSelect);
    embla.on("select", onSelect);
    return () => {
      embla.off("reInit", onSelect);
      embla.off("select", onSelect);
    };
  }, [embla, onSelect]);

  useEffect(() => {
    if (isDesktop) {
      setEmbla(null);
      setActiveIndex(0);
    }
  }, [isDesktop]);

  if (slides.length === 0) {
    return null;
  }

  const activeSlide = slides[activeIndex] ?? slides[0];
  const ctaText = activeSlide.ctaLabel ?? "Sign Up";

  if (isDesktop) {
    const carouselHeight = fillHeight ? "100%" : height;
    const fullHeightStyles = fillHeight
      ? {
          root: { height: "100%" },
          viewport: { height: "100%" },
          container: { height: "100%" },
          slide: { height: "100%" },
        }
      : {};

    const carousel = (
      <Carousel
        height={carouselHeight}
        w="100%"
        maw="100%"
        withControls={multi}
        withIndicators={multi}
        loop={multi}
        plugins={multi ? [autoplay.current] : []}
        onMouseEnter={multi ? autoplay.current.stop : undefined}
        onMouseLeave={multi ? autoplay.current.reset : undefined}
        controlSize={compact ? 20 : 22}
        controlsOffset={compact ? 2 : 4}
        styles={{
          root: {
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            ...fullHeightStyles.root,
          },
          viewport: { maxWidth: "100%", boxSizing: "border-box", ...fullHeightStyles.viewport },
          container: { maxWidth: "100%", ...fullHeightStyles.container },
          controls: {
            paddingInline: 0,
          },
          control: {
            opacity: 0.92,
          },
          indicators: { bottom: compact ? rem(4) : rem(8) },
          indicator: { width: 6, height: 6 },
          slide: fullHeightStyles.slide,
        }}
      >
        {slides.map((slide) => (
          <Carousel.Slide key={slide.id}>
            <DiscoverPromoSlideCard slide={slide} multi={multi} compact={compact} />
          </Carousel.Slide>
        ))}
      </Carousel>
    );

    if (fillHeight) {
      return (
        <Box h="100%" w="100%" style={{ flex: 1, minHeight: 0 }}>
          {carousel}
        </Box>
      );
    }

    return carousel;
  }

  return (
    <Flex
      w="100%"
      maw="100%"
      mih={height}
      h={height}
      align="stretch"
      wrap="nowrap"
      style={{
        borderRadius: rem(12),
        overflow: "hidden",
        boxShadow: "0 4px 16px rgba(15, 23, 42, 0.1)",
        background: "transparent",
        boxSizing: "border-box",
      }}
    >
      <Box
        pos="relative"
        flex={1}
        miw={0}
        mih={0}
        style={{ minHeight: height, backgroundColor: "#fff" }}
      >
        <Carousel
          height="100%"
          w="100%"
          withControls={false}
          withIndicators={false}
          loop={multi}
          plugins={multi ? [autoplay.current] : []}
          slideSize="100%"
          slideGap={0}
          align="start"
          containScroll="trimSnaps"
          getEmblaApi={setEmbla}
          styles={{
            root: { height: "100%", width: "100%", backgroundColor: "#fff" },
            viewport: { height: "100%", backgroundColor: "#fff" },
            container: { height: "100%", backgroundColor: "#fff" },
            slide: { height: "100%", backgroundColor: "#fff" },
          }}
        >
          {slides.map((slide) => (
            <Carousel.Slide key={slide.id}>
              <DiscoverPromoSlideCardMobile slide={slide} />
            </Carousel.Slide>
          ))}
        </Carousel>

        {multi ? (
          <>
            <Flex
              pos="absolute"
              inset={0}
              align="center"
              justify="space-between"
              px={rem(6)}
              style={{ pointerEvents: "none" }}
            >
              <CarouselNavButton
                direction="prev"
                label="Previous promotion"
                onClick={() => embla?.scrollPrev()}
                disabled={!embla}
              />
              <CarouselNavButton
                direction="next"
                label="Next promotion"
                onClick={() => embla?.scrollNext()}
                disabled={!embla}
              />
            </Flex>
            <Flex
              pos="absolute"
              left={0}
              right={0}
              bottom={rem(6)}
              justify="center"
              align="center"
              gap={rem(6)}
              style={{ pointerEvents: "none" }}
            >
              {slides.map((s, i) => (
                <UnstyledButton
                  key={s.id}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === activeIndex ? "true" : undefined}
                  onClick={() => embla?.scrollTo(i)}
                  w={6}
                  h={6}
                  p={0}
                  style={{
                    borderRadius: "50%",
                    background: i === activeIndex ? NAVY : LINE_GRAY,
                    flexShrink: 0,
                    pointerEvents: "auto",
                    border: "none",
                  }}
                />
              ))}
            </Flex>
          </>
        ) : null}
      </Box>

      <UnstyledButton
        component={Link}
        to="/signup"
        aria-label="Sign up"
        display="flex"
        style={{
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
          minWidth: rem(76),
          maxWidth: "26%",
          paddingInline: rem(8),
          background: CTA_BG,
          textDecoration: "none",
          color: "inherit",
          alignSelf: "stretch",
        }}
      >
        <Text fw={700} fz={rem(12)} c={NAVY} ta="center" lh={1.2} style={{ wordBreak: "break-word" }}>
          {ctaText}
        </Text>
      </UnstyledButton>
    </Flex>
  );
}
